/**
 * Mirrors app/constants/products.py — kept in sync manually.
 * When editing, also update the Python side and audit existing invitations.
 *
 * NET — с Phase 3.1 (2026-04-25) тоже управляется через signaris-auth.
 * Роли: admin / tenant_owner / user.
 * (`tenant_viewer` убран 2026-04-28; `tenant_admin` свёрнут в
 * `tenant_owner` 2026-04-29 — обе роли давали одинаковые права в NET.)
 *
 * `admin` для большинства продуктов — это **платформенная** роль Signaris-staff'а,
 * а не клиентская. RoleEditor скрывает её для tenant-admin'ов, см.
 * `PLATFORM_ROLES_BY_PRODUCT` ниже.
 *
 * `ROLE_DESCRIPTIONS` (что роль реально разрешает) — тоже здесь, зеркала в Python
 * нет. Правила для этой карты — в комментарии над ней: тексты берутся из RBAC-кода
 * продукта и формулируются как назначение роли, а не перечень ручек.
 */
/**
 * Литеральный источник истины для ТИПОВ (`as const` → tsc видит точные ключи).
 * Экспортируемый `PRODUCT_ROLES` ниже намеренно оставлен широким: при `as const`
 * выражение `PRODUCT_ROLES[product].filter(...)` в RoleEditor превращается в вызов
 * метода на объединении tuple-типов и перестаёт компилироваться.
 *
 * Порядок ключей = порядок блоков продуктов в RoleEditor (`BUSINESS_PRODUCTS`).
 */
const PRODUCT_ROLES_DEF = {
    desk: ["admin", "manager", "employee"],
    field: ["admin", "reviewer", "employee"],
    brain: ["admin", "employee"],
    listen: ["admin", "reviewer", "viewer"],
    sonar: ["admin", "marketer", "manager"],
    net: ["admin", "tenant_owner", "user"],
    // Phase 3.6 — Hub (таск-трекер). Внутри-проектные роли (owner/editor/viewer)
    // живут в Hub-приложении, не в JWT.
    hub: ["admin", "member", "viewer"],
};
export const PRODUCT_ROLES = PRODUCT_ROLES_DEF;
/**
 * Платформенные (Signaris-staff) роли по продуктам — их выдаёт только
 * superadmin. Tenant-admin их не должен видеть в RoleEditor: даже если
 * UI обойти, backend ответит 403/400.
 *
 * Сейчас в NET точно есть `admin` (cross-tenant tools для Signaris-staff,
 * через `X-Target-Tenant`-header). Для других продуктов нет явной
 * cross-tenant роли — оставляем пустыми, но пометка зарезервирована
 * на случай, если какой-то продукт это введёт.
 */
export const PLATFORM_ROLES_BY_PRODUCT = {
    net: ["admin"],
};
export const PRODUCT_LABELS = {
    desk: "Desk",
    field: "Field",
    brain: "Brain",
    listen: "Listen",
    sonar: "Sonar",
    net: "Net",
    hub: "Hub",
    signaris_auth: "Signaris Auth",
};
/** Id служебного «продукта», которым управляется сам signaris-auth. */
export const SIGNARIS_AUTH_PRODUCT = "signaris_auth";
/** Роли самого identity-провайдера. Зеркало `SIGNARIS_AUTH_ROLES` в products.py. */
export const SIGNARIS_AUTH_ROLES = ["admin", "hr", "superadmin"];
/**
 * Slug организации-штата Signaris. Зеркалит backend `settings.staff_tenant_slug`
 * (дефолт "signaris"). RoleEditor показывает опцию `superadmin` только при
 * редактировании сотрудника штата. Бэк авторитетен (отобьёт 403 при mismatch) —
 * это лишь UX-гейт.
 */
export const STAFF_TENANT_SLUG = "signaris";
/**
 * Роли, которые вправе выдавать кадровик (`signaris_auth:hr`) — при приглашении,
 * одобрении заявки и как условие отключения/возврата учётки (все роли цели ⊆ этому
 * списку). Явный список, не «всё, кроме admin»: `desk:manager` видит все заявки,
 * `listen:reviewer` — все записи, в Sonar любая роль = полный доступ.
 * `field:reviewer` — с прод-выката Field 26.10.2026: видит только точки ТУ из
 * своей карточки, их выдачу кадровиком админы видят в письме. Зеркало
 * `HR_GRANTABLE_ROLES` в app/constants/products.py — парити-тест сверяет.
 * Бэк авторитетен (403).
 */
export const HR_GRANTABLE_ROLES = [
    "desk:employee",
    "field:employee",
    "field:reviewer",
    "brain:employee",
    "listen:viewer",
    "net:user",
    "hub:member",
    "hub:viewer",
];
export function isHrGrantable(product, role) {
    return HR_GRANTABLE_ROLES.includes(`${product}:${role}`);
}
/** Продукты, у которых роли управляются через signaris-auth (RoleEditor). */
export const BUSINESS_PRODUCTS = Object.keys(PRODUCT_ROLES);
/**
 * Все 6 коммерческих продуктов — для выбора purchased_products у tenant.
 * С Phase 3.1 совпадает с BUSINESS_PRODUCTS (NET перевели в PRODUCT_ROLES);
 * до этого net хранился отдельно и докладывался руками — артефакт убран.
 */
export const ALL_PURCHASABLE_PRODUCTS = BUSINESS_PRODUCTS;
/**
 * Брендовые цвета продуктов — из IT_startup/index.html (`:root --p-*`).
 * Если signaris.ru обновит палитру, синкать сюда.
 */
export const PRODUCT_COLORS = {
    desk: "#FF8C00", // orange — заявки
    field: "#FCB813", // amber — задачи
    brain: "#66B345", // green — аналитика
    listen: "#5B9BD5", // blue — продажи
    net: "#E84393", // pink — доступ
    sonar: "#00B4A8", // teal — мониторинг
    hub: "#6C5CE7", // violet — в цвет иконки (штурвал на индиго-фоне), не путать с амбером Field
};
/**
 * Переводы ролей на русский. Ключ — "<product>:<role>".
 * `satisfies` ниже гарантирует, что покрыты ВСЕ роли каталога: новая роль в
 * PRODUCT_ROLES_DEF без метки уронит `npm run build`.
 */
export const ROLE_LABELS = {
    // signaris_auth — служебные роли identity-провайдера. `admin` — tenant-scoped
    // (админ СВОЕЙ организации в auth), `superadmin` — платформенная staff-роль
    // (доступ ко ВСЕМ организациям). Раньше admin звался «Администратор Signaris»,
    // что читалось как «права в орг Signaris» — переименовано для ясности.
    "signaris_auth:admin": "Администратор организации",
    // hr — кадровик организации (docs/HR_PROFILE.md): кадровые данные, приглашения
    // с рядовыми ролями (HR_GRANTABLE_ROLES), отключение/возврат учёток.
    "signaris_auth:hr": "Кадровик",
    "signaris_auth:superadmin": "Суперадминистратор Signaris (все организации)",
    // desk
    "desk:admin": "Администратор",
    "desk:manager": "Менеджер",
    "desk:employee": "Сотрудник",
    // field
    "field:admin": "Администратор",
    "field:reviewer": "Проверяющий",
    "field:employee": "Исполнитель",
    // brain
    "brain:admin": "Администратор",
    "brain:employee": "Аналитик",
    // listen
    "listen:admin": "Администратор",
    "listen:reviewer": "Ревьюер",
    "listen:viewer": "Просмотр",
    // sonar
    "sonar:admin": "Администратор",
    "sonar:marketer": "Маркетолог",
    "sonar:manager": "Менеджер",
    // net (Phase 3.1) — tenant_viewer убран 2026-04-28;
    // tenant_admin → tenant_owner 2026-04-29 (свёрнуты в одну роль).
    "net:admin": "Администратор Signaris",
    "net:tenant_owner": "Администратор организации",
    "net:user": "Пользователь",
    // hub (Phase 3.6) — корпоративный таск-трекер.
    "hub:admin": "Администратор",
    "hub:member": "Участник",
    "hub:viewer": "Наблюдатель",
};
/**
 * Что роль реально разрешает делать — одна фраза, показывается строкой под
 * названием роли в RoleEditor и на странице принятия приглашения.
 *
 * ПРАВИЛА (нарушение = UI врёт про доступ, а это identity-провайдер):
 * 1. Текст берётся из RBAC-кода продукта, а не из головы. Источники — в
 *    комментариях к блокам ниже.
 * 2. Формулировать НАЗНАЧЕНИЕ роли, а не перечень ручек: перечень стареет
 *    каждым релизом продукта, назначение — раз в год.
 * 3. ≤60 символов: длиннее — вторая строка на десктопе, +16px × 20 ролей.
 * 4. `null` — описания сознательно нет: роль не определяет объём доступа,
 *    вместо строки показывается PRODUCT_ROLE_NOTES[product].
 * 5. Меняется RBAC продукта — описание правится тем же тикетом.
 */
export const ROLE_DESCRIPTIONS = {
    // signaris_auth — app/constants/products.py, app/routes/admin_registrations.py
    // (одобрение саморегистраций — самое сильное полномочие tenant-админа).
    "signaris_auth:admin": "Сотрудники, роли, заявки на регистрацию и журнал.",
    "signaris_auth:hr": "Кадровые данные, приглашения с рядовыми ролями, отключение.",
    "signaris_auth:superadmin": "Все организации Signaris и выдача платформенных ролей.",
    // desk — сверено с AXO_bot_web/backend/core/rbac.py (ROLE_PERMISSIONS), 2026-08-27
    "desk:admin": "Всё, что менеджер, плюс сотрудники, роли и журнал.",
    "desk:manager": "Ведёт все заявки, справочник точек и смотрит отчёты.",
    "desk:employee": "Создаёт заявки и видит только свои.",
    // field — сверено с Task_bot_PWA/backend/core/rbac.py, 2026-08-27; reviewer — 2026-10-26
    // по коммиту Field 2745014: права только по точкам из tu_site_ids (кассы этих точек).
    "field:admin": "Задачи, приёмка фотоотчётов, расписания, группы, сотрудники.",
    "field:reviewer": "Ставит задачи своим точкам и принимает их фотоотчёты.",
    "field:employee": "Видит свои задачи и сдаёт по ним фотоотчёты.",
    // brain — сверено с UPPETIT_Neurobot/backend/api/admin/, 2026-08-27.
    // NB: управления сотрудниками в Brain нет (api/admin/users.py → 410 Gone,
    // сотрудники ведутся здесь, в auth) — не писать «управляет пользователями».
    "brain:admin": "Ведёт базу знаний и смотрит отзывы на ответы.",
    "brain:employee": "Задаёт вопросы и получает ответы по базе знаний.",
    // listen — сверено с Listen/app/api/{criteria,cards,devices}.py, 2026-08-27
    "listen:admin": "Всё, что ревьюер, плюс критерии оценки и устройства.",
    "listen:reviewer": "Смотрит диалоги и вручную пересматривает оценку ИИ.",
    "listen:viewer": "Только смотрит карточки и статистику, без правок.",
    // net — сверено с ProxyTG/net-api/app/{deps,routes}, 2026-08-27.
    // net:admin — cross-tenant по ВСЕМ организациям + CRUD VPN-серверов;
    // net:user сам себе выпускает и отзывает профиль (routes/portal_me.py).
    "net:admin": "Полный доступ к NET во всех организациях, включая серверы.",
    "net:tenant_owner": "VPN-профили сотрудников, журнал и состояние серверов.",
    "net:user": "Сам выпускает себе VPN-конфиг и может его отозвать.",
    // hub — сверено с Hub/app/services/project_access.py::can_create_project и
    // Hub/docs/ROLES.md, 2026-09-01. Права в Hub — произведение JWT-роли, org_role,
    // content_role и членства в проекте, поэтому member/viewer описаны через границу.
    // NB: member и viewer расходятся РОВНО в одном — создание проектов и папок, и то
    // лишь при org_role office/tu/franchisee_owner. Везде ещё (обучение, контент,
    // аналитика) они неразличимы — не писать «только смотрит», это была бы ложь.
    "hub:admin": "Все разделы Hub: проекты, оргструктура, обучение, журнал.",
    "hub:member": "Роль по умолчанию; проекты заводят офис, ТУ и франчайзи.",
    "hub:viewer": "Всё то же, но проекты и папки не создаёт никогда.",
    // sonar — SurveyKioskSystem/apps/api/src/middleware/auth.ts: гейт presence-only,
    // роль нигде не сужает доступ. Построчных описаний нет осознанно — см. NOTES.
    "sonar:admin": null,
    "sonar:marketer": null,
    "sonar:manager": null,
};
/**
 * Подпись на уровне продукта — показывается под списком ролей, когда роль
 * не определяет объём доступа внутри продукта.
 */
export const PRODUCT_ROLE_NOTES = {
    sonar: "Все роли Sonar работают с опросами и результатами одинаково — роль задаёт зону ответственности, а не объём доступа.",
    hub: "Права внутри Hub настраиваются в самом Hub — участие в проектах, обучение, контент.",
};
export function roleLabel(product, role) {
    return ROLE_LABELS[`${product}:${role}`] ?? role;
}
export function roleDescription(product, role) {
    return ROLE_DESCRIPTIONS[`${product}:${role}`] ?? null;
}
